/**
 * StatCard.tsx — KPI 统计卡（dashboard 版，最全）+ Stat（抽屉/弹窗内联竖排统计）
 *
 * 移植自原型 design/Novel-admin/src/primitives.jsx 的 StatCard / Stat。
 * StatCard：spark / delta / 纯值三档；长数值自适应缩小避免溢出窄卡片。
 * Stat：dialogs2 版内联竖排（label / value / 可选 delta%）。
 * 蓝本经 window.Charts 取 CountUp / Sparkline，本实施直接 import ~/ui/charts，对齐蓝本；
 * JSX/className/inline-style 与蓝本 1:1。
 */

import { Icons } from '~/lib/icons';
import { CountUp, Sparkline } from '~/ui/charts';

export function StatCard({
  label,
  value,
  delta,
  deltaLabel,
  deltaSuffix = '%',
  spark,
  sparkColor,
  sub,
  hint,
  format = (v) => (typeof v === 'number' ? v.toLocaleString() : String(v)),
  decimals = 0,
}: {
  label: React.ReactNode;
  value: number | string;
  delta?: number;
  deltaLabel?: React.ReactNode;
  /** delta 数值的单位后缀：默认 '%'（环比百分比）；百分点差值传 'pp'，避免 pp 被误标成 % */
  deltaSuffix?: string;
  spark?: Array<number | { value: number }>;
  sparkColor?: string;
  sub?: React.ReactNode;
  hint?: string;
  format?: (v: number | string) => string;
  /** CountUp 动画保留小数位（LTV/CPI 比值等非整数值必传，否则动画终值被取整） */
  decimals?: number;
}) {
  // 长数值自适应缩小，避免溢出窄卡片 (如 5 列网格里的 €1,142,380)
  const display = String(format(value));
  const valueFs =
    display.length > 11 ? 18 : display.length > 9 ? 20 : display.length > 7 ? 22 : undefined;
  return (
    <div
      className="card"
      style={{ display: 'flex', flexDirection: 'column', padding: 14, gap: 8, minWidth: 0 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="stat-label">{label}</div>
        {hint && (
          <span title={hint} style={{ color: 'var(--fg-faint)', cursor: 'help' }}>
            <Icons.IconInfo size={12} />
          </span>
        )}
      </div>
      <div className="stat-value" style={{ fontSize: valueFs, whiteSpace: 'nowrap', minWidth: 0 }}>
        {typeof value === 'number' ? (
          <CountUp value={value} format={(v) => format(v)} decimals={decimals} />
        ) : (
          display
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {delta !== undefined && (
            <div className={`stat-delta ${delta >= 0 ? 'delta-pos' : 'delta-neg'}`}>
              {delta >= 0 ? <Icons.IconArrowUp size={11} /> : <Icons.IconArrowDown size={11} />}
              <span className="mono">
                {Math.abs(delta).toFixed(1)}
                {deltaSuffix}
              </span>
              <span style={{ color: 'var(--fg-faint)', marginLeft: 4 }}>
                {deltaLabel || 'vs 上周'}
              </span>
            </div>
          )}
          {sub && <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{sub}</div>}
        </div>
        {spark && (
          <Sparkline data={spark} color={sparkColor || 'var(--accent)'} width={84} height={26} />
        )}
      </div>
    </div>
  );
}

export function Stat({
  label,
  v,
  delta,
}: {
  label: React.ReactNode;
  v: React.ReactNode;
  delta?: number;
}) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ fontSize: 18, marginTop: 4 }}>
        {v}
      </div>
      {delta !== undefined && (
        <div
          className={`stat-delta ${delta >= 0 ? 'delta-pos' : 'delta-neg'}`}
          style={{ marginTop: 2 }}
        >
          {delta >= 0 ? <Icons.IconArrowUp size={10} /> : <Icons.IconArrowDown size={10} />}
          <span className="mono">{Math.abs(delta).toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
}
